import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO } from "@/hooks/useSEO";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  CheckCircle,
  ShieldOff,
  Smartphone,
  MapPin,
  Timer,
  Link2,
  Tags,
  CalendarClock,
  BarChart3,
  Crown,
  TrendingUp,
  Shield,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import programmeImage from "@/assets/affiliation/programme-affiliation.webp";

// Commission data: +10 Pro subs/month cumulative, 26.70€ each
const commissionData = [
  { month: "M1", value: 267 },
  { month: "M2", value: 534 },
  { month: "M3", value: 801 },
  { month: "M4", value: 1068 },
  { month: "M5", value: 1335 },
  { month: "M6", value: 1602 },
  { month: "M7", value: 1869 },
  { month: "M8", value: 2136 },
  { month: "M9", value: 2403 },
  { month: "M10", value: 2670 },
  { month: "M11", value: 2937 },
  { month: "M12", value: 3204 },
];

const benefits = [
  {
    title: "Gagnez 30 % de commissions à vie",
    text: "Chaque fois qu'un abonnement est acheté via votre lien affilié, vous percevez 30 % récurrents. Tant que votre prospect reste abonné, vous êtes rémunéré.",
  },
  {
    title: "Vos prospects bénéficient de -20 %",
    text: "Vos contacts obtiennent 20% de réduction sur les plans annuels Starter et Pro. Ce qui fait un argument de plus pour déclencher la décision.",
  },
  {
    title: "Aucune condition pour devenir affilié",
    text: "Rejoignez notre programme sans être client Unotif et sans conditions. Vous créez votre compte affilié et vous commencez immédiatement.",
  },
];

const steps = [
  {
    title: "Obtenez votre lien affilié",
    text: "Créez gratuitement votre compte et récupérez votre lien personnel en ajoutant ?via=VOTREID à l'URL de votre choix pour suivre vos prospects.",
  },
  {
    title: "Partagez-le à votre audience",
    text: "Diffusez votre lien dans vos contenus, emails ou réseaux afin que chaque personne qui clique soit définitivement rattachée à votre compte affilié.",
  },
  {
    title: "Touchez vos commissions récurrentes",
    text: "Dès qu'un utilisateur souscrit à une offre premium, vous percevez 30 % de commission et continuez à être rémunéré tant qu'il reste abonné.",
  },
];

const strengths = [
  {
    icon: ShieldOff,
    title: "Zéro spam, zéro algorithme",
    text: "Les notifications Wallet ne passent par aucun filtre et apparaissent directement sur l'écran verrouillé. Ce que vous envoyez touche 100% de l'audience.",
  },
  {
    icon: Smartphone,
    title: "Sans application à télécharger",
    text: "Apple Wallet et Google Wallet sont déjà installés nativement sur tous les smartphones. Vos abonnés ajoutent la carte en un clic.",
  },
  {
    icon: MapPin,
    title: "Notifications géolocalisées",
    text: "Envoyez une alerte automatique quand un abonné passe près de votre boutique ou de votre événement.",
  },
  {
    icon: Timer,
    title: "Créé en quelques minutes",
    text: "Éditeur visuel, aperçu temps réel, aucune compétence technique requise. Votre canal est prêt en moins de 10 minutes.",
  },
  {
    icon: Link2,
    title: "Un lien ou un QR code suffit",
    text: "Partagez sur vos réseaux, en email, en live ou sur une présentation. L'ajout au Wallet se fait en un geste.",
  },
  {
    icon: Tags,
    title: "Tags & segmentation",
    text: "Créez plusieurs audiences sur une même carte grâce aux tags, et envoyez des notifications adaptées à chaque segment.",
  },
  {
    icon: CalendarClock,
    title: "Programmation automatique",
    text: "Planifiez vos communications à l'avance. Vos notifications partent au bon moment, sans intervention de votre part.",
  },
  {
    icon: BarChart3,
    title: "Statistiques détaillées",
    text: "Abonnés, diffusions, clics : tout est visible d'un coup d'œil. Mesurez l'impact réel de chaque campagne et affinez votre stratégie.",
  },
  {
    icon: Crown,
    title: "Un canal qui vous appartient",
    text: "Vous construisez une audience qui vous appartient pour toujours, sans dépendre des algorithmes ou d'une plateforme tierce.",
  },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-lg">
        <p className="text-sm font-bold text-[#074155]">{label}</p>
        <p className="text-sm font-medium text-[#0C94A6]">{payload[0].value} €/mois</p>
      </div>
    );
  }
  return null;
};

export default function Affiliation() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({
        title: "Email invalide",
        description: "Veuillez saisir une adresse email valide.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("submit-affiliate", {
        body: { email: trimmed, honeypot },
      });
      if (error) throw error;
      navigate("/programme-affiliation-merci");
    } catch (err) {
      console.error(err);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Programme d'affiliation Unotif – 30% de commissions récurrentes à vie"
        description="Devenez ambassadeur Unotif et gagnez 30% de commissions récurrentes à vie sur chaque abonnement recommandé. Aucune condition requise, gratuit."
        canonical="/affiliation"
      />
      <Header />

      {/* ============ HERO ============ */}
      <section className="relative pt-24 lg:pt-40 pb-4 overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
              Devenez ambassadeur du canal marketing qui bat l'emailing{" "}
              <span className="text-[#0c97a9]">(100 % de visibilité)</span>.
            </h1>

            <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
              Recommandez <span className="font-semibold text-[#0c97a9]">Unotif</span> et gagnez 30% de commissions récurrentes à vie sur chaque abonnement, chaque mois, sans plafond.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col items-center">
              <div className="flex flex-col gap-3 w-full max-w-xl mx-auto">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre adresse email"
                  className="h-12 text-base md:text-base bg-white border-gray-200 w-full"
                  disabled={loading}
                />
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                />
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem" className="w-full">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base px-4 sm:px-6 h-12 w-full whitespace-nowrap"
                  >
                    {loading ? "Envoi…" : "Gagnez des commissions dès maintenant"}
                  </Button>
                </MovingBorderWrapper>
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                👉 Aucune condition requise · Gratuit
              </p>
            </form>
          </div>
        </div>
      </section>


      {/* ============ BÉNÉFICES ============ */}
      <section className="bg-[#FAFBFB] pt-8 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 transition-[transform,border-color,box-shadow] duration-150 ease-out hover:-translate-y-2 hover:border-[#0C94A6]/20 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12),0_4px_16px_-4px_rgba(0,0,0,0.08)]"
              >
                <div className="w-12 h-12 rounded-full bg-[#DBEFF2] flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-[#0C94A6]" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                  {benefit.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COMMENT DÉMARRER ============ */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image */}
            <div>
              <img
                src={programmeImage}
                alt="Comment démarrer en tant qu'affilié Unotif"
                loading="lazy"
                className="rounded-2xl w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>

            {/* Steps */}
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
                Comment démarrer en tant qu'affilié
              </h2>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
                Trois étapes simples pour commencer à générer vos premières commissions :
              </p>

              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#0C94A6] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-2">
                        {step.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {step.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POTENTIEL DE GAINS ============ */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex justify-center mb-6">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
                Simulation de revenus
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155]">
              Gagnez jusqu'à 220 € par nouvel utilisateur
            </h2>
          </div>

          {/* 2 columns */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left: Revenue info */}
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-[#0C94A6]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#0C94A6]">
                  Potentiel de revenus
                </span>
              </div>

              <p className="text-4xl sm:text-5xl font-bold text-[#074155] mb-4">
                26,70€<span className="text-lg sm:text-xl font-medium text-[#495456]">/mois</span>
              </p>

              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                Vous percevez 30% de commission récurrente sur chaque abonnement recommandé. Un seul abonné au plan Starter vous rapporte 8,70€ chaque mois, à vie. Un abonné Pro : 26,70€/mois.
              </p>
              <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-6">
                Avec seulement 10 abonnés Pro actifs, vous générez 267 € récurrents par mois à vie. La clé ? La rétention.
              </p>

              {/* Retention block */}
              <div className="rounded-xl bg-[#DBEFF2]/50 border border-[#0C94A6]/20 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-[#0C94A6]" />
                  <span className="text-sm font-bold text-[#022226]">L'effet rétention Wallet</span>
                </div>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                  Une carte Wallet partagée devient un canal propriétaire. Une fois qu'un créateur y construit son audience, il ne peut plus s'en passer : supprimer Unotif reviendrait à perdre son accès direct à ses abonnés. Cette dépendance structurelle crée une rétention élevée et sécurise vos commissions dans le temps.
                </p>
              </div>
            </div>

            {/* Right: Chart */}
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]">
              <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-1">
                Évolution de vos commissions (en €/mois)
              </h3>
              <p className="text-[14px] font-medium text-[#495456] mb-6">
                Simulation avec +10 nouveaux abonnés Pro par mois
              </p>

              <div className="h-[300px] sm:h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={commissionData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0c97a9" />
                        <stop offset="100%" stopColor="#70ceda" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: "#495456", fontWeight: 500 }}
                      axisLine={{ stroke: "#E5E7EB" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#495456", fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(12, 148, 166, 0.05)" }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={40}>
                      {commissionData.map((_, index) => (
                        <Cell key={index} fill="url(#barGradient)" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <p className="text-xs sm:text-[13px] font-medium text-[#495456] mt-4 leading-relaxed">
                Hypothèse : 10 abonnés Pro/mois, taux de rétention 85%. Les commissions sont cumulatives et récurrentes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POURQUOI UNOTIF (9 blocs) ============ */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex justify-center mb-6">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
                Les points forts
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Un canal que votre audience adoptera naturellement
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-3xl mx-auto">
              Vous ne recommandez pas un simple outil. Vous recommandez un nouveau canal marketing direct.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {strengths.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="group p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 transition-[transform,border-color,box-shadow] duration-150 ease-out hover:-translate-y-2 hover:border-[#0C94A6]/20 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12),0_4px_16px_-4px_rgba(0,0,0,0.08)]"
                >
                  <div className="w-12 h-12 rounded-full bg-[#0C94A6]/15 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#0C94A6]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CTA COMMUNAUTÉ ============ */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="max-w-4xl mx-auto rounded-2xl bg-[#0C94A6] p-8 sm:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Rejoignez notre communauté !
            </h2>
            <p className="text-base font-medium text-white/90 mb-8">
              Échangez avec d'autres affiliés, partagez vos stratégies et accédez à des ressources exclusives.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-[#0C94A6] hover:bg-white/90 font-semibold text-base px-8 py-6 shadow-lg"
            >
              <a href="https://www.facebook.com/groups/unotif">
                Rejoignez notre groupe Facebook
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
